import { useCallback, useState } from "react";

/**
 * Like useState but persisted to localStorage.
 * Falls back to `initialValue` if the key is absent or JSON-parse fails.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [stored, setStored] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      return item !== null ? (JSON.parse(item) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStored((prev) => {
        const next = value instanceof Function ? value(prev) : value;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* quota exceeded — ignore */
        }
        return next;
      });
    },
    [key],
  );

  return [stored, setValue];
}
